import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Separator,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Input,
} from '@predoc/cow';
import { useListProfiles } from '../../../profiles/hooks/profilesAPI';
import { useChangePatientProfile } from '../../hooks/patientsAPI';
import { ExistingPatient } from '../../types';
import { Info, Hospital, ChevronDown, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@predoc/cow';
import { Permission, useHasPermission } from '@/utils/roles';
import { getProfileColor } from '@/utils';
import { isProfileChangeAllowed } from '../../utils/requestStatusUtils';
import { useDebounce } from '@/hooks';

interface PatientProfileSelectorProps {
  patient: ExistingPatient;
}

export const PatientProfileSelector = ({
  patient,
}: PatientProfileSelectorProps) => {
  const [selectedProfile, setSelectedProfile] = useState({
    id: patient.organizationProfile.id,
    displayName: patient.organizationProfile.displayName,
    avatarType: patient.organizationProfile.avatarType,
  });
  const [pendingProfileId, setPendingProfileId] = useState<string | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { hasPermission: canEdit } = useHasPermission(Permission.MOVE_PATIENTS);
  const changePatientProfile = useChangePatientProfile();

  const canChangeProfile = isProfileChangeAllowed(
    patient.organizationStatus || {},
  );

  const { data: profilesData, isLoading } = useListProfiles({
    assignedProfilesOnly: true,
    searchTerms: debouncedSearch ? [debouncedSearch] : undefined,
    pagination: { offset: 0, limit: 100 },
  });

  const profiles = profilesData?.data || [];

  const handleSelect = useCallback(
    (profile: { id: string; displayName: string; avatarType: string }) => {
      setOpen(false);
      setSearchTerm('');

      if (profile.id === patient.organizationProfile.id) {
        setSelectedProfile(profile);
        return;
      }

      setSelectedProfile(profile);
      setPendingProfileId(profile.id);
      setShowConfirmationModal(true);
    },
    [patient.organizationProfile.id],
  );

  const handleConfirmChange = async () => {
    if (!pendingProfileId) return;
    try {
      await changePatientProfile.mutateAsync({
        patientId: patient.id,
        organizationProfileId: pendingProfileId,
      });
      setShowConfirmationModal(false);
      setPendingProfileId(null);
    } catch {
      setShowConfirmationModal(false);
      setPendingProfileId(null);
      setSelectedProfile({
        id: patient.organizationProfile.id,
        displayName: patient.organizationProfile.displayName,
        avatarType: patient.organizationProfile.avatarType,
      });
    }
  };

  const handleCancelChange = () => {
    setShowConfirmationModal(false);
    setPendingProfileId(null);
    setSelectedProfile({
      id: patient.organizationProfile.id,
      displayName: patient.organizationProfile.displayName,
      avatarType: patient.organizationProfile.avatarType,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setSearchTerm('');
    setOpen(next);
  };

  if (!canEdit) {
    const profileColor = getProfileColor(
      patient.organizationProfile.avatarType,
    );
    return (
      <div className="flex items-center gap-2">
        <Hospital className="size-4" style={{ color: profileColor }} />
        <span className="text-sm">
          {patient.organizationProfile.displayName}
        </span>
      </div>
    );
  }

  const triggerColor = getProfileColor(selectedProfile.avatarType);

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="border-input bg-background h-9 w-full max-w-[250px] justify-between rounded-md border px-3 font-normal"
            disabled={!canChangeProfile}
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <Hospital
                className="size-4 shrink-0"
                style={{ color: triggerColor }}
              />
              <span className="truncate text-sm">
                {selectedProfile.displayName}
              </span>
            </div>
            <ChevronDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="max-w-[250px] p-0" align="start">
          <div className="p-2">
            <Input
              placeholder="Search profiles..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center p-4">
                <span className="text-muted-foreground text-sm">
                  Loading...
                </span>
              </div>
            ) : profiles.length === 0 ? (
              <div className="flex items-center justify-center p-4">
                <span className="text-muted-foreground text-sm">
                  No profiles found.
                </span>
              </div>
            ) : (
              <div className="p-1">
                {profiles.map((profile) => {
                  const color = getProfileColor(profile.avatarType);
                  const isSelected = profile.id === selectedProfile.id;
                  return (
                    <Button
                      key={profile.id}
                      variant="ghost"
                      className="h-auto w-full justify-start gap-2 px-2 py-1.5"
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        handleSelect({
                          id: profile.id,
                          displayName: profile.displayName,
                          avatarType: profile.avatarType,
                        });
                      }}
                    >
                      <Hospital className="size-4 shrink-0" style={{ color }} />
                      <span className="flex-1 text-left text-sm">
                        {profile.displayName}
                      </span>
                      {isSelected && <Check className="size-4 shrink-0" />}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Dialog
        open={showConfirmationModal}
        onOpenChange={(open: boolean) => !open && handleCancelChange()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Profile Change</DialogTitle>
          </DialogHeader>
          <Separator />
          <div>
            <Alert variant="accent" className="p-4">
              <Info className="size-4" />
              <AlertDescription>
                You&apos;re about to move this patient to another profile. Only
                users assigned to the new profile will have access to this
                patient.
              </AlertDescription>
            </Alert>
          </div>
          <Separator />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelChange}
              disabled={changePatientProfile.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmChange}
              disabled={changePatientProfile.isPending}
            >
              {changePatientProfile.isPending ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
